/* eslint-disable no-nested-ternary */
import { useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { icons } from '~/assets';
import Button from '~/components/button';
import Container from '~/components/container';
import ContainerFlex from '~/components/containerFlex';
import Dialog from '~/components/dialog';
import Input, { MaskTypes } from '~/components/input';
import Loading from '~/components/loading';
import Pagination from '~/components/pagination';
import Select from '~/components/select';
import TableList from '~/components/tableList';
import useApi, { ApiMethod } from '~/hooks/useApi';
import { IListColumn } from '~/models/listCol';
import Endpoints from '~/services/endpoints';
import { httpRequest } from '~/services/httpRequest';
import { cpfCnpjExtendedMask, IDefaultReturn, phoneMask } from '~/utils/masks';
import { getCompaniesHttp } from './actions';
import * as St from './styles';

interface Address {
  bairro: string;
  cep: string;
  cidade: string;
  complemento: string;
  logradouro: string;
  numero: string;
  pais: string;
  uf: string;
}

interface IEstablishment {
  cpf: string;
  cnpj: string;
  email: string;
  endereco: Address;
  id: string;
  telefone: string;
}

interface IEstablishmentResponse {
  currentPage: number;
  data: IEstablishment[];
  pageSize: number;
  totalItens: number;
  totalPages;
}

interface IForm {
  name: string;
  code: IDefaultReturn;
}

type ListMode = 'establishments' | 'businessGroups';
type StatusFilter = 'all' | 'true' | 'false';

const BUSINESS_GROUP_TAB_INDEX = 2;

const LIST_MODE_OPTIONS = [
  { id: 'establishments', value: 'Estabelecimentos' },
  { id: 'businessGroups', value: 'Grupos Econômicos' },
];
const STATUS_OPTIONS = [
  { id: 'all', value: 'Todos' },
  { id: 'true', value: 'Ativo' },
  { id: 'false', value: 'Inativo' },
];

interface PrincipalGroupCompany {
  nickname: string;
  document: string;
}

interface BusinessGroup {
  active: boolean;
  id: string;
  name: string;
  principalGroupCompany?: PrincipalGroupCompany;
}

const TeuPagEstablishment = () => {
  const { state: locationState } = useLocation<{ listMode?: ListMode }>();
  const [form, setForm] = useState<IForm>({
    code: { masked: '', unmasked: '' },
    name: '',
  } as IForm);
  const [listMode, setListMode] = useState<ListMode>(
    locationState?.listMode ?? 'establishments',
  );
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [businessGroupName, setBusinessGroupName] = useState('');
  const [idEstablishment, setIdEstablishment] = useState('');
  const [openDialogConfirmation, setOpenDialogConfirmation] = useState(false);
  const [list, setList] = useState([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState(1);
  const [openDialog, setOpenDialog] = useState(false);
  const [message, setMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const history = useHistory();

  const getSearchEstablishments = async (params?) => {
    setIsLoading(true);
    const formattedParams = Object.fromEntries(
      Object.entries(params).filter(([_, value]) => !!value),
    );

    const response = await getCompaniesHttp({
      limite: 10,
      page: currentPage,
      ...formattedParams,
    });

    setIsLoading(false);

    if (response.status === 200) {
      const { data, totalSize, page, pageSize, totalPages } = response.data;
      setCurrentPage(page);
      setTotalPages(totalPages);
      return data;
    }
  };
  useEffect(() => {
    if (listMode === 'establishments') {
      handleSubmit();
    } else {
      searchBusinessGroups();
    }
  }, [currentPage]);

  const RequestDeleteEstablishments = useApi(
    Endpoints.establishments.deleteEstablishments.replace(
      'PARAM_ID',
      idEstablishment,
    ),
    ApiMethod.DELETE,
  );

  console.log(idEstablishment);

  const customHeader = [
    {
      id: 1,
      value: 'Nome/Razão Social',
      maxChar: 20,
    },
    {
      id: 2,
      value: 'CPF/CNPJ',
      maxChar: 18,
    },
    {
      id: 3,
      value: 'E-mail',
      maxChar: 15,
    },
    {
      id: 4,
      value: 'Celular',
      maxChar: 15,
    },
    {
      id: 5,
      value: 'Tipo',
      maxChar: 15,
    },
    {
      id: 6,
      value: 'Empresa Principal',
      maxChar: 10,
    },
    {
      id: 7,
      value: 'Ativo',
      maxChar: 10,
    },
    {
      id: 8,
      value: 'Ação',
      maxChar: 10,
    },
    // {
    //   id: 6,
    //   value: 'Excluir',
    //   maxChar: 10,
    // },
  ];

  const businessGroupHeader = [
    {
      id: 1,
      value: 'Nome do Grupo',
      maxChar: 40,
    },
    {
      id: 2,
      value: 'Empresa Principal',
      maxChar: 30,
    },
    {
      id: 3,
      value: 'Ação',
      maxChar: 10,
    },
  ];

  const handleChange = ({
    target: { value, name },
  }: React.ChangeEvent<HTMLInputElement>) => {
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const isButtonDisabled = useMemo(() => {
    if (list.length) return false;
    const isCpfOrCnpj =
      form.code?.masked?.length === 14 ||
      form.code?.masked?.length === 18 ||
      form.code?.masked?.length === 22;

    const isNameFilled = form.name && form.name.trim().length > 0;

    return statusFilter !== 'all' || !(isCpfOrCnpj || isNameFilled);
  }, [form, statusFilter]);

  const handleSubmit = async () => {
    const data = await getSearchEstablishments({
      cnpj: form.code.unmasked ?? null,
      nome: form.name ?? null,
      ...(statusFilter !== 'all' && { active: statusFilter }),
    });

    const partnershipTranslations = {
      COMMERCIAL: 'Comercial',
      AGREEMENT: 'Convênio',
      ALL: 'Todos',
    };

    if (data) {
      const newList = [];

      data.forEach((data) => {
        const items: IListColumn[] = [
          {
            border: true,
            title: 'Nome/Razão social',
            value: data.name,
            maxChar: 20,
          },
          {
            title: 'CPF/CNPJ',
            value: data.document
              ? cpfCnpjExtendedMask(data.document).masked
              : data.cnpj
              ? cpfCnpjExtendedMask(data.document).masked
              : '',
            textAlign: 'center',
          },
          {
            title: 'E-mail',
            value: data.email,
            maxChar: 20,
            textAlign: 'center',
          },
          {
            title: 'Telefone',
            value: phoneMask(data?.phone).masked,
            maxChar: 15,
            textAlign: 'center',
          },
          {
            title: 'Tipo',
            value: partnershipTranslations[data.partnership] || 'Nenhum',
            maxChar: 15,
            textAlign: 'center',
          },
          {
            title: 'Empresa Principal',
            value: data.isBusinessGroup ? 'Sim' : 'Não',
            maxChar: 10,
            textAlign: 'center',
          },
          {
            title: 'Ativo',
            value: data.active ? 'Sim' : 'Não',
            maxChar: 10,
            textAlign: 'center',
          },
          {
            item: data,
            searchAction: () => goToDetail(data),
          },
          // {
          //   item: data,
          //   maxChar: 5,
          //   action: (id, item) => {
          //     setIdEstablishment(id);
          //     setOpenDialogConfirmation(true);
          //   },
          // },
        ];
        newList.push(items);
      });

      setList(newList);
    }
  };

  const searchBusinessGroups = async () => {
    setIsLoading(true);
    try {
      const response = await httpRequest<BusinessGroup[]>({
        url: Endpoints.companies.getBusinessGroupsV2,
        tryNewWay: true,
        body: {
          nome: businessGroupName || undefined,
          page: currentPage,
          limite: 10,
          sort: 'CREATED_DATE_DESC',
          showParameters: true,
        },
      });

      setIsLoading(false);

      if (response.status === 200 && response.data) {
        const groups = Array.isArray(response.data)
          ? response.data
          : (response.data as { data: BusinessGroup[] }).data ?? [];

        const totalPagesFromResponse =
          (response.data as { totalPages?: number }).totalPages ?? 1;
        setTotalPages(totalPagesFromResponse);

        const groupList = groups.map((group) => {
          const items: IListColumn[] = [
            {
              border: true,
              title: 'Nome do Grupo',
              value: group.name,
              maxChar: 40,
            },
            {
              title: 'Empresa Principal',
              value: group.principalGroupCompany
                ? `${group.principalGroupCompany.nickname} - ${
                    cpfCnpjExtendedMask(group.principalGroupCompany.document)
                      .masked
                  }`
                : '-',
              maxChar: 30,
              textAlign: 'center',
            },
            {
              item: group,
              searchAction: () => goToBusinessGroupDetail(group),
            },
          ];
          return items;
        });

        setList(groupList);
      }
    } catch {
      setIsLoading(false);
    }
  };

  const goToBusinessGroupDetail = async (group: BusinessGroup) => {
    setIsLoading(true);
    try {
      const response = await httpRequest({
        url: Endpoints.companies.getBusinessGroupById.replace(
          'PARAM_GROUP_ID',
          group.id,
        ),
      });

      setIsLoading(false);

      if (response.status === 200 && response.data) {
        history.push('/teu-pag/estabelecimento/detail', {
          groupId: group.id,
          businessGroupData: response.data,
        });
      }
    } catch {
      setIsLoading(false);
    }
  };

  const handleListModeChange = async (mode: ListMode) => {
    setListMode(mode);
    history.replace(history.location.pathname, { listMode: mode });
    setList([]);
    setCurrentPage(1);
    setTotalPages(1);

    if (mode === 'businessGroups') {
      setIsLoading(true);
      try {
        const response = await httpRequest<BusinessGroup[]>({
          url: Endpoints.companies.getBusinessGroupsV2,
          tryNewWay: true,
          body: {
            page: 1,
            limite: 10,
            sort: 'CREATED_DATE_DESC',
            showParameters: true,
          },
        });

        setIsLoading(false);

        if (response.status === 200 && response.data) {
          const groups = Array.isArray(response.data)
            ? response.data
            : (response.data as { data: BusinessGroup[] }).data ?? [];

          const totalPagesFromResponse =
            (response.data as { totalPages?: number }).totalPages ?? 1;
          setTotalPages(totalPagesFromResponse);

          const groupList = groups.map((group) => {
            const items: IListColumn[] = [
              {
                border: true,
                title: 'Nome do Grupo',
                value: group.name,
                maxChar: 40,
              },
              {
                title: 'Empresa Principal',
                value: group.principalGroupCompany
                  ? `${group.principalGroupCompany.nickname} - ${
                      cpfCnpjExtendedMask(group.principalGroupCompany.document)
                        .masked
                    }`
                  : '-',
                maxChar: 30,
                textAlign: 'center',
              },
              {
                item: group,
                searchAction: () => goToBusinessGroupDetail(group),
              },
            ];
            return items;
          });

          setList(groupList);
        }
      } catch {
        setIsLoading(false);
      }
    } else {
      handleSubmit();
    }
  };

  const goToDetail = (item) => {
    history.push('/teu-pag/estabelecimento/detail', {
      ...item,
    });
  };

  const goToRegister = () => {
    history.push('/teu-pag/estabelecimento/detail');
  };

  const goToNewBusinessGroup = () => {
    history.push('/teu-pag/estabelecimento/detail', {
      defaultTab: BUSINESS_GROUP_TAB_INDEX,
    });
  };

  const deleteEstablishment = async () => {
    setIsLoading(true);
    try {
      const response = await httpRequest({
        url: Endpoints.companies.deleteCompany.replace(
          'PARAM_ID',
          idEstablishment,
        ),
        method: ApiMethod.DELETE,
      });
      if (response.status >= 200) {
        setOpenDialog(true);
        setOpenDialogConfirmation(false);
        setMessage('Cliente excluído com sucesso!');
        setIsLoading(false);
      } else {
        setOpenDialog(true);
        setMessage('Erro ao excluir!');
        setIsLoading(false);
      }
    } catch (error) {
      console.log(error);
      setIsLoading(false);
    }
  };

  return (
    <>
      <Loading status={isLoading} />
      <Dialog
        open={openDialog}
        icon={icons.exclamation}
        positiveAction={() => {
          setOpenDialog(false);
          handleSubmit();
        }}
        message={message}
        positiveLabel="Entendi"
      />
      <Dialog
        icon={icons.warning}
        open={openDialogConfirmation}
        negativeAction={() => {
          setOpenDialogConfirmation(false);
        }}
        positiveAction={deleteEstablishment}
        Loading={isLoading}
        buttonDisabledPositive={RequestDeleteEstablishments.loading}
        positiveLabel="SIM"
        negativeLabel="NÃO"
        message="Deseja mesmo excluir o cliente?"
      />
      <Container>
        <St.BoxCard>
          {listMode === 'establishments' ? (
            <ContainerFlex row space>
              <ContainerFlex marginRight={16}>
                <Input
                  label="Nome/Razão-social"
                  value={form.name}
                  onChange={(e) => {
                    setForm({
                      ...form,
                      name: e.target.value,
                    });
                  }}
                />
              </ContainerFlex>
              <ContainerFlex marginRight={16}>
                <Input
                  label="CPF/CNPJ"
                  name="code"
                  value={form.code?.masked}
                  onChange={handleChange}
                  mask={MaskTypes.CPF_CNPJ_EXTENDED}
                />
              </ContainerFlex>
              <ContainerFlex marginRight={16} style={{ maxWidth: 200 }}>
                <Select
                  label="Tipo de consulta"
                  options={LIST_MODE_OPTIONS}
                  value={listMode}
                  onChange={(id) => handleListModeChange(id as ListMode)}
                />
              </ContainerFlex>
              <ContainerFlex marginRight={16} style={{ maxWidth: 200 }}>
                <Select
                  label="Status"
                  options={STATUS_OPTIONS}
                  value={statusFilter}
                  onChange={(id) => setStatusFilter(id as ListMode)}
                />
              </ContainerFlex>

              <ContainerFlex row gap="30">
                <Button
                  fitContent
                  onClick={() => handleSubmit()}
                  disabled={isButtonDisabled}
                >
                  Consultar
                </Button>
                <Button fitContent onClick={goToRegister}>
                  Novo Parceiro
                </Button>
              </ContainerFlex>
            </ContainerFlex>
          ) : (
            <ContainerFlex row space>
              <ContainerFlex marginRight={16}>
                <Input
                  label="Nome"
                  value={businessGroupName}
                  onChange={(e) => setBusinessGroupName(e.target.value)}
                />
              </ContainerFlex>
              <ContainerFlex marginRight={16} style={{ maxWidth: 200 }}>
                <Select
                  label="Tipo de consulta"
                  options={LIST_MODE_OPTIONS}
                  value={listMode}
                  onChange={(id) => handleListModeChange(id as ListMode)}
                />
              </ContainerFlex>
              <ContainerFlex row gap="30">
                <Button fitContent onClick={() => searchBusinessGroups()}>
                  Consultar
                </Button>
                <Button fitContent onClick={goToNewBusinessGroup}>
                  Novo Grupo
                </Button>
              </ContainerFlex>
            </ContainerFlex>
          )}
        </St.BoxCard>
        {list.length <= 0 ? (
          <St.BoxCard>
            <ContainerFlex center>
              <span>Cliente TeuPag não encontrado</span>
            </ContainerFlex>
          </St.BoxCard>
        ) : (
          <St.BoxCard>
            <ContainerFlex flex="0.2">
              {!list.length ? (
                <ContainerFlex center marginTop={30}>
                  <span>Cliente TeuPag não encontrado</span>
                </ContainerFlex>
              ) : null}
              <ContainerFlex>
                {list.length > 0 && (
                  <>
                    <ContainerFlex>
                      <TableList
                        customerHeader={
                          listMode === 'establishments'
                            ? customHeader
                            : businessGroupHeader
                        }
                        list={list}
                      />
                    </ContainerFlex>
                    <ContainerFlex marginTop={20} center flex={1}>
                      <Pagination
                        totalPages={totalPages}
                        initialCurrentPage={currentPage}
                        onCurrentPageChange={setCurrentPage}
                      />
                    </ContainerFlex>
                  </>
                )}
              </ContainerFlex>
            </ContainerFlex>
          </St.BoxCard>
        )}
      </Container>
    </>
  );
};

export default TeuPagEstablishment;
